var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { useThrottle } from '@linked/controls';
import { Chip, alpha, IconButton, InputBase, makeStyles } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import SearchIcon from '@material-ui/icons/Search';
import { define, Model, type } from '@type-r/models';
import { useBoundLink, useLinked, useModel } from '@type-r/react';
import * as React from 'react';
import { useEffect, useMemo } from 'react';
import { div } from '../common';
import { Flex } from '../flex';
import { asDate } from '../formatters';
import { CompactInput } from './compact';
import { FormSelect } from './horizontal';
import { Input } from './inputs';
import { Option } from './select';
import { SelectMulti } from './selecti-multi';
/**
 * **SearchInput** is an special text input with search icon and throttled update of the enclosed value.
 * It updates the linked value after 1 second when user stopped typing.
 *
 * This input can be used to make an instant search and filtering on linked value change.
 */
export var SearchInput = function (_a) {
    var $value = _a.$value, props = __rest(_a, ["$value"]);
    var classes = useStyles();
    var $inputValue = useBoundLink($value)
        .onChange(useThrottle(function (x) { $value.set(x.trim()); }, 1000, [$value.value]));
    return (React.createElement("div", { className: classes.search },
        React.createElement("div", { className: classes.searchIcon },
            React.createElement(SearchIcon, null)),
        React.createElement(InputBase, __assign({ placeholder: "Search\u2026", classes: {
                root: classes.inputRoot,
                input: classes.inputInput,
            }, inputProps: { 'aria-label': 'search' } }, $inputValue.props, props))));
};
var useStyles = makeStyles(function (theme) { return ({
    inputRoot: {
        color: 'inherit',
        width: '100%'
    },
    inputInput: {
        paddingLeft: 24,
        transition: theme.transitions.create('width'),
        width: '100%',
        fontSize: 14
    },
    search: {
        position: 'relative',
        borderRadius: theme.shape.borderRadius,
        backgroundColor: alpha(theme.palette.common.white, 0.15),
        '&:hover': {
            backgroundColor: alpha(theme.palette.common.white, 0.25),
        },
        marginLeft: 0,
        width: '100%'
    },
    searchIcon: {
        height: '100%',
        position: 'absolute',
        pointerEvents: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    chip: {
        marginRight: 4,
        marginBottom: 4,
        overflow: 'hidden',
    },
    formSelect: {
        maxWidth: 250
    }
}); });
export var FiltersList = function (_a) {
    var filter = _a.filter;
    var labels = useLabels(filter);
    var classes = useStyles();
    return (React.createElement(FiltersContainer, null, filter.keys().map(function (key) {
        if (key !== 'id' && key !== 'filterAll') {
            var value_1 = filter[key];
            var isValid_1 = value_1 && (value_1.isValid ? value_1.isValid() : true);
            var label = isValid_1 && value_1.asLabel();
            return isValid_1 ?
                React.createElement(Chip, { label: label === undefined ? labels[key] : labels[key] + ': ' + label, onDelete: function () { return value_1.delete(); }, title: value_1.asTitle(), className: classes.chip })
                : null;
        }
    })));
};
var FiltersContainer = div({
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    width: '100%',
});
var isValid = function (value) { return value && (value.isValid ? value.isValid() : true); };
function useLabels(filter) {
    var displayLabels = filter.constructor.displayLabels;
    return useMemo(function () {
        return filter.mapObject(function (_, key) {
            var displayLabel = displayLabels && displayLabels[key];
            if (displayLabel)
                return displayLabel;
            var x = key.replace(/([A-Z])/g, " $1");
            return x.charAt(0).toUpperCase() + x.slice(1);
        });
    }, [filter.constructor]);
}
export var AddFilter = function (_a) {
    var filter = _a.filter, filterAllPlaceholder = _a.filterAllPlaceholder;
    var labels = useLabels(filter);
    var classes = useStyles();
    var emptyFilters = useMemo(function () {
        var emptyFilters = ['filterAll'];
        for (var key in filter.attributes) {
            if (key !== 'id' && key !== 'filterAll') {
                if (!isValid(filter[key]) && !filter[key].isInitial) {
                    emptyFilters.push(key);
                }
            }
        }
        return emptyFilters;
    }, [filter['_changeToken']]);
    var $filterBy = useLinked(emptyFilters[0]);
    useEffect(function () {
        if (!emptyFilters.includes($filterBy.value)) {
            $filterBy.set(emptyFilters[0]);
        }
    }, [emptyFilters]);
    if (!$filterBy.value) {
        return React.createElement("div", null);
    }
    var Attr = filter['_attributes'][$filterBy.value];
    var EnterAttr = Attr.type === DateRangeFilter ? EnterRange :
        Attr.type === MultiSelectFilter ? EnterMultiSelect :
            Attr.type === BooleanFilter ? EnterBoolean :
                EnterText;
    var filterAll = $filterBy.value === 'filterAll';
    return (React.createElement(Flex, { row: true },
        React.createElement(FormSelect, { "$value": $filterBy, classes: {
                root: classes.formSelect
            } }, emptyFilters.map(function (key) {
            return React.createElement(Option, { value: key, key: key }, labels[key] || Attr.name);
        })),
        filterAll ?
            React.createElement(SearchInput, { "$value": filter.$.filterAll, placeholder: filterAllPlaceholder || "Filter by all fields" })
            :
                React.createElement(EnterAttr, { key: $filterBy.value, filterValue: filter[$filterBy.value], onEnter: function (range) { return filter[$filterBy.value] = range; } })));
};
export var EnterText = function (_a) {
    var onEnter = _a.onEnter;
    var state = useModel(TextFilter);
    return React.createElement(React.Fragment, null,
        React.createElement(Input, { "$value": state.$.value, placeholder: "Add filter", onKeyPress: function (e) {
                if (e.charCode === 13) {
                    onEnter(state.clone());
                }
            } }),
        React.createElement(IconButton, { "aria-label": "Add", size: "small", onClick: function () { return onEnter(state.clone()); } },
            React.createElement(AddIcon, null)));
};
export var EnterBoolean = function (_a) {
    var onEnter = _a.onEnter;
    var state = useModel(BooleanFilter);
    return React.createElement(React.Fragment, null,
        React.createElement(IconButton, { "aria-label": "Add", size: "small", onClick: function () {
                state.$.value.true();
                onEnter(state.clone());
            } },
            React.createElement(AddIcon, null)));
};
var FilterModel = /** @class */ (function (_super) {
    __extends(FilterModel, _super);
    function FilterModel() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    FilterModel.prototype.asTitle = function () {
        return this.asLabel();
    };
    FilterModel.attributes = {
        isInitial: type(Boolean).value(false),
    };
    return FilterModel;
}(Model));
export { FilterModel };
var TextFilter = /** @class */ (function (_super) {
    __extends(TextFilter, _super);
    function TextFilter() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    TextFilter.prototype.delete = function () {
        this.value = undefined;
    };
    TextFilter.prototype.asLabel = function () {
        return this.value;
    };
    TextFilter.attributes = __assign({}, FilterModel.attributes, { value: type(String).required });
    TextFilter = __decorate([
        define
    ], TextFilter);
    return TextFilter;
}(FilterModel));
export { TextFilter };
var BooleanFilter = /** @class */ (function (_super) {
    __extends(BooleanFilter, _super);
    function BooleanFilter() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    BooleanFilter.prototype.delete = function () {
        this.value = undefined;
    };
    BooleanFilter.prototype.asLabel = function () {
        return undefined;
    };
    BooleanFilter.attributes = __assign({}, FilterModel.attributes, { value: type(Boolean).required });
    BooleanFilter = __decorate([
        define
    ], BooleanFilter);
    return BooleanFilter;
}(FilterModel));
export { BooleanFilter };
var DateRangeFilter = /** @class */ (function (_super) {
    __extends(DateRangeFilter, _super);
    function DateRangeFilter() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    DateRangeFilter.prototype.delete = function () {
        this.from = undefined;
        this.to = undefined;
    };
    DateRangeFilter.prototype.asLabel = function () {
        return asDate(this.from) + ' - ' + asDate((this.to));
    };
    DateRangeFilter.attributes = __assign({}, FilterModel.attributes, { from: type(Date).null.required, to: type(Date).null.required });
    DateRangeFilter = __decorate([
        define
    ], DateRangeFilter);
    return DateRangeFilter;
}(FilterModel));
export { DateRangeFilter };
export var SelectDateRange = function (_a) {
    var model = _a.model, $from = _a.$from, $to = _a.$to;
    var $start = model ? model.$.from : $from;
    var $end = model ? model.$.to : $to;
    return (React.createElement(React.Fragment, null,
        React.createElement(Input, { "$value": $start, date: true, placeholder: "From", selectsStart: true, endDate: $end.value }),
        React.createElement(Input, { "$value": $end, date: true, placeholder: "To", selectsEnd: true, startDate: $start.value, endDate: $end.value, minDate: $start.value })));
};
export var CompactSelectDateRange = function (_a) {
    var fromLabel = _a.fromLabel, $from = _a.$from, toLabel = _a.toLabel, $to = _a.$to;
    return React.createElement(React.Fragment, null,
        React.createElement(CompactInput, { label: fromLabel, "$value": $from, date: true, placeholder: "From", selectsStart: true, endDate: $to.value }),
        React.createElement(CompactInput, { label: toLabel, "$value": $to, date: true, placeholder: "To", selectsEnd: true, startDate: $from.value, endDate: $to.value, minDate: $from.value }));
};
export var EnterRange = function (_a) {
    var onEnter = _a.onEnter;
    var state = useModel(DateRangeFilter);
    React.useEffect(function () {
        if (state.isValid()) {
            onEnter({ from: state.from, to: state.to.setUTCHours(23, 59, 59, 999) });
        }
    }, [state['_changeToken']]);
    return (React.createElement(SelectDateRange, { model: state }));
};
export var EnterMultiSelect = function (_a) {
    var onEnter = _a.onEnter, filterValue = _a.filterValue;
    return (React.createElement(SelectMulti, { options: filterValue.options, "$selected": filterValue.$.selected, onMenuClosed: function () {
            if (filterValue.selected && filterValue.selected.length) {
                filterValue.$.isDone.set(true);
                onEnter(filterValue.clone());
            }
        } }));
};
var MultiSelectFilter = /** @class */ (function (_super) {
    __extends(MultiSelectFilter, _super);
    function MultiSelectFilter() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    MultiSelectFilter.prototype.delete = function () {
        this.$.selected.set(undefined);
        this.$.isDone.set(false);
    };
    MultiSelectFilter.prototype.asLabel = function () {
        return this.selected.length > 1 ? "Selected " + this.selected.length : this.selected[0];
    };
    MultiSelectFilter.prototype.asTitle = function () {
        return this.selected.map(function (x) { return x; }).join();
    };
    MultiSelectFilter.prototype.isSuccessful = function (value) {
        return !this.selected || !this.selected.length || this.selected.includes(value);
    };
    MultiSelectFilter.attributes = __assign({}, FilterModel.attributes, { options: [String], selected: type([String]).required, isDone: type(Boolean).check(function (x) { return x && this.selected && this.selected.length; }) });
    MultiSelectFilter = __decorate([
        define
    ], MultiSelectFilter);
    return MultiSelectFilter;
}(FilterModel));
export { MultiSelectFilter };
export var filterByMultiSelect = function (multiSelect, value) {
    return !multiSelect || !multiSelect.selected || !multiSelect.selected.length
        || multiSelect.selected.includes(value);
};
//# sourceMappingURL=search.js.map